import { useEffect, useMemo, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import { timelineService } from '@/services/timeline'
import { useData } from '@/contexts/data'
import { editPostFormSchema, type EditPostFormData, type EditPostModalProps } from './types'
import { TOAST_SUCCESS, TOAST_ERROR } from './constants'
import {
  FormTerritoriesField,
  FormTopicsField,
  FormContextsField,
  FormTypeField,
  FormPartnersField,
  FormSubmitButton,
} from './components'

export function EditPostModal({ open, onOpenChange, post, onSuccess }: EditPostModalProps) {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    territories,
    territoriesLoading,
    contexts,
    contextsLoading,
    topics,
    topicsLoading,
    partners,
    partnersLoading,
  } = useData()

  const defaultValues = useMemo<EditPostFormData>(
    () => ({
      territories: post.territories || [],
      topics: post.topics || [],
      contexts: post.contexts || [],
      type: (post.type as EditPostFormData['type']) || undefined,
      partners: (post.metadata as { partners?: string[] })?.partners || [],
    }),
    [post]
  )

  const methods = useForm<EditPostFormData>({
    resolver: zodResolver(editPostFormSchema),
    defaultValues,
  })

  const { reset, formState: { isDirty } } = methods

  useEffect(() => {
    if (open) {
      reset(defaultValues)
    }
  }, [open, defaultValues, reset])

  const handleClose = (value: boolean) => {
    if (!value && isSubmitting) return
    onOpenChange(value)
  }

  const onSubmit = async (data: EditPostFormData) => {
    try {
      setIsSubmitting(true)
      const updatedPost = await timelineService.update(post.id, {
        territories: data.territories,
        contexts: data.contexts,
        topics: data.topics,
        type: data.type,
        partners: data.partners,
      })
      toast(TOAST_SUCCESS)
      onSuccess(updatedPost)
      onOpenChange(false)
    } catch {
      toast({ ...TOAST_ERROR, variant: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const territoryItems = useMemo(() => territories.map((t) => ({ id: t.id, name: t.name })), [territories])
  const contextItems = useMemo(() => contexts.map((c) => ({ id: c.id, name: c.name })), [contexts])
  const topicItems = useMemo(() => topics.map((t) => ({ id: t.id, name: t.name })), [topics])
  const partnerItems = useMemo(() => partners.map((p) => ({ id: p.id, name: p.name })), [partners])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <FormProvider {...methods}>
          <form className="space-y-4" onSubmit={methods.handleSubmit(onSubmit)}>
            <FormTerritoriesField items={territoryItems} loading={territoriesLoading} />
            <FormTopicsField items={topicItems} loading={topicsLoading} />
            <FormContextsField items={contextItems} loading={contextsLoading} />
            <FormTypeField />
            <FormPartnersField items={partnerItems} loading={partnersLoading} />
            <FormSubmitButton isSubmitting={isSubmitting} isDirty={isDirty} />
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
